// components/contracts/ContractProjectCard.js
// Componente per gestire il collegamento Contratto → Progetto/Cantiere

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Building2, 
  Link2, 
  Plus, 
  ArrowRight, 
  Loader2,
  AlertCircle,
  CheckCircle2,
  HardHat
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';

const ContractProjectCard = ({ contract, contractId, onProjectLinked }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checkingCompatible, setCheckingCompatible] = useState(true);
  const [compatibleProject, setCompatibleProject] = useState(null);
  const [linkError, setLinkError] = useState(null);
  
  // Stato del contratto
  const isApproved = contract?.status === 'approvato';
  const hasLinkedProject = !!contract?.project_id;
  
  // Verifica se esiste un progetto compatibile
  useEffect(() => {
    const checkCompatibleProject = async () => {
      if (!contractId || hasLinkedProject) {
        setCheckingCompatible(false);
        return;
      }
      
      try {
        setCheckingCompatible(true);
        const response = await api.get(`/contracts/${contractId}/compatible-project`);
        const data = response.data;
        
        if (data.has_compatible_project) {
          setCompatibleProject({
            id: data.project_id,
            number: data.project_number,
            reason: data.reason,
            matchReasons: data.match_reasons
          });
        } else {
          setCompatibleProject(null);
        }
      } catch (error) {
        console.error('Error checking compatible project:', error);
      } finally {
        setCheckingCompatible(false);
      }
    };
    
    checkCompatibleProject();
  }, [contractId, hasLinkedProject]);
  
  // Collega progetto esistente
  const handleLinkProject = async () => {
    if (!compatibleProject?.id) return;
    
    try {
      setLoading(true);
      setLinkError(null);
      
      const response = await api.post(`/contracts/${contractId}/link-project`, {
        project_id: compatibleProject.id
      });
      const data = response.data;
      
      if (data.success) {
        toast.success(`Progetto ${data.project_number} collegato con successo`);
        if (onProjectLinked) {
          onProjectLinked(data.project_id, data.project_number);
        }
      } else {
        throw new Error(data.message || 'Errore nel collegamento');
      }
    } catch (error) {
      console.error('Error linking project:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Errore nel collegamento del progetto';
      setLinkError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };
  
  // Crea nuovo progetto
  const handleCreateProject = async () => {
    try {
      setLoading(true);
      setLinkError(null);
      
      const response = await api.post(`/contracts/${contractId}/create-project`);
      const data = response.data;
      
      if (data.success) {
        if (data.action === 'created') {
          toast.success(`Progetto ${data.project_number} creato con successo`);
          if (onProjectLinked) {
            onProjectLinked(data.project_id, data.project_number);
          }
        } else if (data.action === 'existing') {
          toast.info(`Progetto già esistente: ${data.project_number}`);
          if (onProjectLinked) {
            onProjectLinked(data.project_id, data.project_number);
          }
        } else if (data.action === 'link_suggested') {
          // Esiste un progetto compatibile - aggiorna lo stato
          setCompatibleProject({
            id: data.project_id,
            number: data.project_number,
            reason: 'link_suggested'
          });
          toast.warning(data.message);
        }
      } else {
        if (data.action === 'link_suggested') {
          setCompatibleProject({
            id: data.project_id,
            number: data.project_number,
            reason: 'link_suggested'
          });
          toast.warning(data.message);
        } else {
          throw new Error(data.message || 'Errore nella creazione');
        }
      }
    } catch (error) {
      console.error('Error creating project:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Errore nella creazione del progetto';
      setLinkError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };
  
  // Vai al progetto
  const handleGoToProject = () => {
    if (contract?.project_id) {
      navigate(`/projects/${contract.project_id}`);
    }
  };

  return (
    <Card className="card-abitalo" data-testid="contract-project-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <HardHat className="h-5 w-5 text-[#FF7819]" />
          Progetto / Cantiere
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        
        {/* Caso 1: Contratto non approvato */}
        {!isApproved && (
          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg" data-testid="project-not-approved">
            <AlertCircle className="h-5 w-5 text-gray-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm text-gray-600">
                Il progetto potrà essere creato dopo l'approvazione del contratto.
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Status attuale: <Badge variant="outline" className="ml-1">{contract?.status}</Badge>
              </p>
            </div>
          </div>
        )}
        
        {/* Caso 2: Contratto approvato con progetto collegato */}
        {isApproved && hasLinkedProject && (
          <div className="space-y-3" data-testid="project-linked">
            <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
              <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-800">
                  Progetto collegato
                </p>
                <p className="text-xs text-green-600">
                  ID: {contract.project_id?.substring(0, 8)}...
                </p>
              </div>
            </div>
            
            <Button 
              onClick={handleGoToProject}
              className="w-full bg-[#FF7819] hover:bg-[#e56a10]"
              data-testid="go-to-project-btn"
            >
              <Building2 className="h-4 w-4 mr-2" />
              Vai al Progetto
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        )}
        
        {/* Caso 3: Contratto approvato, no progetto, verifica in corso */}
        {isApproved && !hasLinkedProject && checkingCompatible && (
          <div className="flex items-center justify-center p-4">
            <Loader2 className="h-5 w-5 animate-spin text-[#FF7819]" />
            <span className="ml-2 text-sm text-gray-500">Verifica progetti compatibili...</span>
          </div>
        )}
        
        {/* Caso 4: Contratto approvato, esiste progetto compatibile */}
        {isApproved && !hasLinkedProject && !checkingCompatible && compatibleProject && (
          <div className="space-y-3" data-testid="compatible-project-found">
            <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <Link2 className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  Progetto compatibile trovato
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  <strong>{compatibleProject.number}</strong>
                </p>
                {compatibleProject.matchReasons && (
                  <p className="text-xs text-amber-600 mt-1">
                    Match: {compatibleProject.matchReasons.join(', ')}
                  </p>
                )}
              </div>
            </div>
            
            <Button 
              onClick={handleLinkProject}
              disabled={loading}
              className="w-full bg-[#FF7819] hover:bg-[#e56a10]"
              data-testid="link-project-btn"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Link2 className="h-4 w-4 mr-2" />
              )}
              Collega Progetto Esistente
            </Button>
            
            {linkError && (
              <p className="text-xs text-red-600 mt-1">{linkError}</p>
            )}
          </div>
        )}
        
        {/* Caso 5: Contratto approvato, nessun progetto compatibile */}
        {isApproved && !hasLinkedProject && !checkingCompatible && !compatibleProject && (
          <div className="space-y-3" data-testid="no-compatible-project">
            <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
              <Plus className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm text-blue-800">
                  Nessun progetto esistente. Puoi crearne uno nuovo da questo contratto.
                </p>
              </div>
            </div>
            
            <Button 
              onClick={handleCreateProject}
              disabled={loading}
              className="w-full bg-[#FF7819] hover:bg-[#e56a10]"
              data-testid="create-project-btn"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Crea Progetto / Cantiere
            </Button>
            
            {linkError && (
              <p className="text-xs text-red-600 mt-1">{linkError}</p>
            )}
          </div>
        )}
        
      </CardContent>
    </Card>
  );
};

export default ContractProjectCard;
